// Contact avatar handling for staged-action views.
//
// Older persisted views baked a third-party avatar URL (api.dicebear.com
// initials) into the contact icon. Those views re-request that URL on every
// render, which discloses the contact's name — and on some paths their bare
// email address — plus the viewer's IP to an external service. We rewrite such
// URLs to a locally rendered initials avatar so no request leaves the app.
//
// New views already arrive with a local `data:` avatar from the gateway
// (apps/http-gateway/src/lib/initials-avatar.ts). Keep the two behaviors in sync.

const LEGACY_AVATAR_HOST = 'api.dicebear.com'

/** Up to two uppercase initials from a display name or a bare email address. */
function extractInitials(seed: string): string {
  const words = seed.trim().split(/\s+/).filter(Boolean)
  if (words.length >= 2) {
    const first = words[0].replace(/[^\p{L}\p{N}]/gu, '')
    const last = words[words.length - 1].replace(/[^\p{L}\p{N}]/gu, '')
    const initials = (first.slice(0, 1) + last.slice(0, 1)).toUpperCase()
    if (initials) return initials
  }
  const alnum = seed.replace(/[^\p{L}\p{N}]/gu, '')
  return alnum.slice(0, 2).toUpperCase() || '?'
}

/** Deterministic background hue so the same seed always gets the same color. */
function seedHue(seed: string): number {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (Math.imul(31, hash) + seed.charCodeAt(i)) | 0
  }
  return (hash >>> 0) % 360
}

/** Build a self-contained `data:image/svg+xml` initials avatar seeded by `seed`. */
export function initialsAvatarDataUri(seed: string): string {
  const initials = extractInitials(seed)
  const background = `hsl(${seedHue(seed)}, 60%, 45%)`
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">' +
    `<rect width="64" height="64" fill="${background}"/>` +
    '<text x="50%" y="50%" fill="#ffffff" font-family="sans-serif" font-size="28" font-weight="500" ' +
    `text-anchor="middle" dominant-baseline="central">${initials}</text>` +
    '</svg>'
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

// A legacy URL renders to the same avatar every time, so cache the rewrite —
// `Icon` runs this on every render, for every tag, in list-heavy views.
const rewriteCache = new Map<string, string>()

/**
 * Pass an icon source through unchanged, unless it is a legacy third-party
 * initials-avatar URL — those are rewritten to a local avatar seeded by the
 * same name so the outbound request never happens.
 */
export function resolveContactAvatarSrc(src: string | undefined): string | undefined {
  if (!src || !src.includes(LEGACY_AVATAR_HOST)) return src
  const cached = rewriteCache.get(src)
  if (cached) return cached
  let seed = ''
  try {
    seed = new URL(src).searchParams.get('seed') ?? ''
  } catch {
    // Not a parseable URL; fall back to an empty-seed avatar.
  }
  const rewritten = initialsAvatarDataUri(seed)
  rewriteCache.set(src, rewritten)
  return rewritten
}
